import React, { useEffect, useState } from "react";
import {
  BareButton,
  Button,
  FileSelector,
  useFileUploads,
} from "@setel/web-ui";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSentEmail } from "~/services/queries/zendesk.queries";
import {
  IEmailField,
  IEmailProps,
  IFileProps,
  MAX_UPLOAD_SIZE,
} from "~/lib/types/email.type";
import { fileUploadChecker } from "~/lib/utils/helper/fileUploadChecker";
import { runTimeConfig } from "~/lib/utils/runtime-config";

// COMPONENTS
import LoadingContainer from "~/lib/components/ui/LoadingContainer";
import ContactInput from "./ContactInput";
import ContactCard from "./ContactCard";
import { useAuth } from "~/lib/utils/hooks/useAuth";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import Container from "~/lib/components/layout/Container";
import { useLocaleNavigate } from "../../link";

const initialValues: IEmailField = {
  name: "",
  phoneNo: "",
  email: "",
  message: "",
  attachment: undefined,
};

const EmailUs = () => {
  const navigate = useLocaleNavigate();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [isSuccessEmail, setIsSuccessEmail] = useState<boolean>(false);
  const { PHONENO, EMAIL } = runTimeConfig.ZENDESK_REQUEST_FIELDKEY;

  const validationSchema = Yup.object({
    name: Yup.string().required(t("thisFieldIsRequired")),
    phoneNo: Yup.string().required(t("thisFieldIsRequired")),
    email: Yup.string()
      .email(t("invalidEmailFormat"))
      .required(t("thisFieldIsRequired")),
    message: Yup.string().required(t("thisFieldIsRequired")),
  });

  const {
    mutate: mutateEmail,
    isLoading: isLoadingEmail,
    isError: isError,
    isSuccess: isSuccess,
  } = useSentEmail();

  useEffect(() => {
    if (isError) {
      setIsSuccessEmail(false);
      alert(t("connectionErrorPleaseTryAgain"));
    }
    if (isSuccess) {
      setIsSuccessEmail(true);
    }
  }, [isError, isSuccess, isSuccessEmail]);

  const { items, addFile, hasPendingRequest } = useFileUploads({
    uploadOperation: "local",
    maxCount: 1,
    validateBeforeUpload: (file) => {
      if (file.size > MAX_UPLOAD_SIZE) {
        return t("fileSizeTooBigPleaseChooseAnotherFile");
      } else if (fileUploadChecker(file.type)) {
        return t("unsupportedFileFormatPleaseChooseAnotherFile");
      }
      return undefined;
    },
    onChange: (results) => formik.setFieldValue("attachment", results[0]),
  });

  const item: IFileProps = items[0];

  const onSubmit = (values: IEmailField) => {
    const { name, phoneNo, email, message, attachment } = values;

    const payload: IEmailProps = {
      request: {
        subject:
          process.env.NODE_ENV === "development"
            ? "[DEV] HELP CENTRE TICKET"
            : "HELP CENTRE TICKET",
        comment: {
          body: message,
        },
        priority: "normal",
        requester: {
          email: email,
          name: name,
        },
        custom_fields: [
          { id: PHONENO, value: phoneNo },
          { id: EMAIL, value: email },
        ],
      },
      attachment: attachment,
    };
    mutateEmail(payload);
  };

  const formik = useFormik<IEmailField>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  const isValidForm =
    formik.values !== initialValues &&
    formik.isValid &&
    !(hasPendingRequest && formik.isSubmitting);

  useEffect(() => {
    formik.setFieldValue("name", profile?.fullName);
    formik.setFieldValue("phoneNo", profile?.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return (
    <>
      <Container>
        <div className="py-5 md:py-46 flex justify-center">
          <div className="md:min-w-[740px] w-full 2xl:px-[128px] md:px-[150px] xl:px-[200px] px-[20px]">
            {isLoadingEmail ? (
              <LoadingContainer />
            ) : isSuccessEmail ? (
              <div className="px-3">
                <h1 className="font-semibold text-article-subtitle">
                  {t("thankYou")}
                </h1>
                <p className="mt-5 mb-6 text-mediumgrey text-lg">
                  {t("feedbackReceived")}
                </p>
                <BareButton
                  className="text-brand text-lg font-medium underline"
                  onClick={() => {
                    formik.resetForm();
                    window.location.reload();
                  }}
                >
                  {t("submitAnotherEmail")}
                </BareButton>
                <BareButton
                  className="text-brand text-lg font-medium underline pt-4"
                  onClick={() => navigate("/")}
                >
                  {t("backHome")}
                </BareButton>
              </div>
            ) : (
              <>
                <h2 className="font-medium md:font-semibold text-xl md:text-article-subtitle">
                  {t("emailUs")}
                </h2>
                <div className="py-5">
                  {profile && (
                    <ContactCard
                      name={profile.fullName}
                      phoneNo={profile.phone}
                    />
                  )}
                  <form onSubmit={formik.handleSubmit}>
                    {!profile && (
                      <>
                        <ContactInput
                          inputLabel={t("name")}
                          inputType="name"
                          id="name"
                          placeHolder="John Doe"
                          testId="name-input"
                          onChange={formik.handleChange}
                          value={formik.values.name}
                          error={
                            formik.touched.name && formik.errors.name
                              ? formik.errors.name
                              : undefined
                          }
                        />
                        <ContactInput
                          inputLabel={t("phoneNo")}
                          inputType="phoneNo"
                          id="phoneNo"
                          placeHolder="+60123456789"
                          testId="phone-input"
                          onChange={formik.handleChange}
                          value={formik.values.phoneNo}
                          error={
                            formik.touched.phoneNo && formik.errors.phoneNo
                              ? formik.errors.phoneNo
                              : undefined
                          }
                        />
                      </>
                    )}
                    <ContactInput
                      inputLabel={t("emailAdd")}
                      inputType="email"
                      id="email"
                      placeHolder="example@example.com"
                      testId="email-input"
                      onChange={formik.handleChange}
                      value={formik.values.email}
                      error={
                        formik.touched.email && formik.errors.email
                          ? formik.errors.email
                          : undefined
                      }
                    />
                    <ContactInput
                      inputLabel={t("message")}
                      inputType="textarea"
                      id="message"
                      placeHolder={t("contactExperience")}
                      testId="message-input"
                      onChange={formik.handleChange}
                      value={formik.values.message}
                      error={
                        formik.touched.message && formik.errors.message
                          ? formik.errors.message
                          : undefined
                      }
                    />

                    <div className="py-5">
                      <FileSelector
                        id="attachment"
                        data-testid="uploadFile-input"
                        description={t("fileFormatUpTo", {
                          fileFormat: "PNG, JPG, GIF",
                          size: "10MB",
                        })}
                        file={item?.file}
                        onFilesSelected={(newFiles) => addFile(newFiles[0])}
                        onRemove={item?.onRemove}
                        onRetry={item?.onRetry}
                        uploadStatus={item?.uploadStatus}
                        uploadProgress={item?.uploadProgress || 0.3}
                        errorMessage={item?.errorMessage}
                      />
                    </div>
                    <div className=" flex flex-col-reverse md:flex-row  md:space-x-2 md:space-y-0">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        {t("cancel")}
                      </Button>
                      <Button
                        className="mb-5 md:mb-0"
                        variant="primary"
                        type="submit"
                        disabled={!isValidForm}
                        data-testid="submit-button"
                      >
                        {t("submitEmail")}
                      </Button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default EmailUs;
